import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import logoLeasing from "../../../../assets/images/drawable-header.png";
import logoMoney from "../../../../assets/images/money.png";
import logoKSM from "../../../../assets/images/ksm.png";
import dayjs from "dayjs";

// ============================================================
// SHARED UTILS
// ============================================================

const findSignatureImage = async (imageList, userId) => {
  if (!imageList || !userId) return null;
  const expectedName = `lawyer/user/license_lawyer/public/${userId}.png`;
  const signature = imageList.find((item) => item.name === expectedName);
  if (!signature) return null;
  try {
    const response = await fetch(signature.url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const getCompanyData = (companiesList, companyId) => {
  if (!companiesList || !companyId) return null;
  const idMap = { 4: 1, 5: 2 }; // 4 ใช้ 1, 5 ใช้ 2
  const id = idMap[companyId] || companyId;
  return companiesList.find((c) => c.id === id);
};

const getApproverInfo = (currentUserId, nameLawyers) => {
  const approverMapping = { 16: 13, 17: 14, 18: 15 };
  const approverId = approverMapping[currentUserId] || null;
  if (!approverId) return null;
  const approver = nameLawyers?.find((item) => item.id === approverId);
  return { approverId, approverData: approver };
};

const canApprove = (currentUserId) => {
  return [16, 17, 18, 1, 6].includes(parseInt(currentUserId));
};

const groupByTMBILL = (selectedRows) => {
  const grouped = {};
  selectedRows.forEach((row) => {
    if (!grouped[row.TMBILL]) grouped[row.TMBILL] = [];
    grouped[row.TMBILL].push(row);
  });
  return grouped;
};

const prepareTableData = (
  tmbill,
  billDetail,
  convertDateThai,
  currencyFormatPoint,
) => {
  const billDataList = billDetail.filter((item) => item.TMBILL === tmbill);
  if (!billDataList || billDataList.length === 0)
    return { tableData: [], totalAmount: 0 };

  const groupedByContno = {};
  let totalAmount = 0;

  billDataList.forEach((billData) => {
    if (!billData.workExpenseList?.length) return;
    billData.workExpenseList.forEach((expense) => {
      const contno = expense.CONTNO || "-";
      if (!groupedByContno[contno]) {
        groupedByContno[contno] = {
          CONTNO: contno,
          created_date: expense.NETPAY_DT
            ? dayjs(expense.NETPAY_DT).add(543, "year").format("DD MMM YYYY")
            : "-",
          expenses: [],
          subtotal: 0,
        };
      }
      const amount = parseFloat(expense.NETPAY) || 0;
      groupedByContno[contno].expenses.push({
        description: expense.description || "-",
        amount,
        expenseTypeId: expense.expenseTypeId,
      });
      groupedByContno[contno].subtotal += amount;
      totalAmount += amount;
    });
  });

  const tableData = [];
  let rowIndex = 1;

  Object.values(groupedByContno).forEach((group) => {
    group.expenses.sort((a, b) => a.expenseTypeId - b.expenseTypeId);
    group.expenses.forEach((expense, i) => {
      tableData.push([
        i === 0 ? rowIndex.toString() : "",
        i === 0 ? group.CONTNO : "",
        i === 0 ? group.created_date : "",
        expense.description,
        currencyFormatPoint(expense.amount),
      ]);
    });
    tableData.push(["", "", "", "รวม", currencyFormatPoint(group.subtotal)]);
    rowIndex++;
  });

  if (Object.keys(groupedByContno).length > 1) {
    tableData.push([
      "",
      "",
      "",
      "รวมทั้งหมด",
      currencyFormatPoint(totalAmount),
    ]);
  }

  return { tableData, totalAmount };
};

// ============================================================
// ใบเสร็จ UTILS
// ============================================================

const numberToThaiText = (amount) => {
  const numbers = [
    "ศูนย์",
    "หนึ่ง",
    "สอง",
    "สาม",
    "สี่",
    "ห้า",
    "หก",
    "เจ็ด",
    "แปด",
    "เก้า",
  ];
  const units = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน", "ล้าน"];

  if (!amount || amount === 0) return "ศูนย์บาทถ้วน";
  const [intPart, decPart] = Number(amount).toFixed(2).split(".");

  const convertGroup = (num) => {
    let str = "";
    String(num)
      .split("")
      .reverse()
      .forEach((d, i) => {
        const n = parseInt(d);
        if (n === 0) return;
        if (i === 1 && n === 1) str = "สิบ" + str;
        else if (i === 1 && n === 2) str = "ยี่สิบ" + str;
        else str = numbers[n] + units[i] + str;
      });
    return str;
  };

  const millions = Math.floor(parseInt(intPart) / 1000000);
  const remainder = parseInt(intPart) % 1000000;
  let result = "";
  if (millions > 0) result += convertGroup(millions) + "ล้าน";
  if (remainder > 0) result += convertGroup(remainder);
  result += "บาท";
  const satang = parseInt(decPart);
  result += satang === 0 ? "ถ้วน" : convertGroup(satang) + "สตางค์";
  return result;
};

const formatDateThai = (dateStr) => {
  if (!dateStr) return "-";
  const d = dayjs(dateStr);
  const months = [
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
  ];
  return `${d.date()} ${months[d.month()]} ${d.year() + 543}`;
};

const drawDottedLine = (pdf, x1, y, x2) => {
  pdf.setLineDashPattern([1, 1], 0);
  pdf.setLineWidth(0.25);
  pdf.line(x1, y, x2, y);
  pdf.setLineDashPattern([], 0);
};

// ============================================================
// ใบเสร็จ
// ============================================================
const drawReceiptPage = (
  pdf,
  { record, currencyFormatPoint, companyData, dataExport },
) => {
  console.log(dataExport);

  const setFont = (style = "normal", size = 12) => {
    pdf.setFont("THSarabunNew", style);
    pdf.setFontSize(size);
  };

  const pageW = 210;
  const mL = 5;
  const mR = 5;
  const cW = pageW - mL - mR; // 200mm
  const oY = 0;
  // ====== ชื่อบริษัท + ชื่อเอกสาร (ไม่มีกรอบนอก) ======
  const companyName = companyData.company_name;
  setFont("bold", 18);
  pdf.text(companyName, pageW / 2, oY + 10, { align: "center" });

  setFont("bold", 16);
  pdf.text("ใบเบิกเงินทดรองจ่าย", pageW / 2, oY + 18, { align: "center" });

  // ====== ข้อมูลด้านขวา ======
  const rX = mL + cW - 2;
  setFont("bold", 14);
  pdf.text(`เลขที่เอกสาร  ${record?.DOCNO || "-"}`, rX, oY + 28, {
    align: "right",
  });
  pdf.text(`วันที่เอกสาร  ${formatDateThai(record?.DOCDT)}`, rX, oY + 34, {
    align: "right",
  });
  pdf.text(
    `วันที่กำหนดคืนเงิน  ${formatDateThai(record?.DUEDT)}`,
    rX,
    oY + 40,
    { align: "right" },
  );

  // ====== ข้อมูลด้านซ้าย ======
  const branchName = record?.LOCAT || "-";

  setFont("bold", 14);
  pdf.text("รหัสสาขา", mL + 2, oY + 34);
  pdf.text(branchName, mL + 28, oY + 34);
  pdf.text("ผู้ขอเบิก", mL + 2, oY + 40);
  pdf.text(record?.NAME_LAWYER || "-", mL + 28, oY + 40);

  // ====== ตาราง ======
  const tStartY = oY + 43;
  const formattedAmt = currencyFormatPoint
    ? currencyFormatPoint(record?.TOTAMT)
    : Number(record?.TOTAMT || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
      });

  // ความกว้างคอลัมน์: No.(14) + รายการ(162) + จำนวนเงิน(24) = 200
  const colNo = 12;
  const colAmt = 24;
  const colDes = cW - colNo - colAmt; // 162

  pdf.autoTable({
    head: [["No.", "รายการ", "จำนวนเงิน"]],
    body: [["1.", record?.MEMO1 || "-", formattedAmt]],
    startY: tStartY,
    styles: {
      font: "THSarabunNew",
      fontSize: 14,
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
      lineColor: [0, 0, 180],
      lineWidth: 0.4,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 14,
      halign: "center",
      lineColor: [0, 0, 180],
      lineWidth: 0.4,
    },
    columnStyles: {
      0: { halign: "center", cellWidth: colNo },
      1: { halign: "left", cellWidth: colDes },
      2: { halign: "right", cellWidth: colAmt },
    },
    bodyStyles: { minCellHeight: 28 },
    margin: { left: mL, right: mR },
    tableLineColor: [0, 0, 180],
    tableLineWidth: 0.4,
  });

  let y = pdf.lastAutoTable.finalY;

  // ====== แถวรวม (กรอบเดียวกับตาราง) ======
  const amountText = numberToThaiText(record?.TOTAMT || 0);

  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.4);
  // กรอบแถวรวม — ให้ขอบซ้าย-ขวา-ล่างตรงกับตาราง
  pdf.rect(mL, y, cW, 10);
  // เส้นแบ่งก่อนคอลัมน์ "รวมทั้งสิ้น"
  const dividerX = mL + cW - colAmt - 38; // ตำแหน่งแบ่ง "รวมทั้งสิ้น"
  pdf.line(dividerX + 38, y, dividerX + 38, y + 10);

  setFont("bold", 14);
  // ข้อความตัวอักษร (กลางช่องซ้าย)
  pdf.text(amountText, mL + (dividerX - mL) / 2, y + 7, { align: "center" });
  // "รวมทั้งสิ้น"
  pdf.text("รวมทั้งสิ้น", dividerX + 20, y + 7);
  // ยอดเงิน (ชิดขวา)
  pdf.text(formattedAmt, mL + cW - 2, y + 7, { align: "right" });
  const coverH = 38;
  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.rect(mL, y, cW, coverH);
  y += 10;

  // ====== Checkbox ======
  y += 6;
  const isCash = record?.CHKFLAG === "1";
  const isBank = record?.CHKFLAG === "2";
  const drawCheckbox = (pdf, x, y, checked) => {
    const size = 3.5; // ขนาดของช่อง Checkbox
    pdf.setDrawColor(0, 0, 180); // สีน้ำเงินตามแบบของคุณ
    pdf.setLineWidth(0.25);
    pdf.rect(x, y, size, size); // วาดกรอบสี่เหลี่ยม

    if (checked) {
      pdf.setLineWidth(0.4);
      // วาดเครื่องหมายถูก (V-shape) ข้างใน
      pdf.line(x + 0.5, y + 1.8, x + 1.5, y + 2.8);
      pdf.line(x + 1.5, y + 2.8, x + 3, y + 0.5);
    }
  };

  drawCheckbox(pdf, mL + 5, y - 3.5, isCash);
  setFont("normal", 14);
  pdf.text("เงินสด CASH", mL + 12, y);

  drawCheckbox(pdf, mL + 42, y - 3.5, isBank);
  setFont("normal", 14);
  pdf.text("เงินโอน BANK", mL + 49, y);

  // ====== ธนาคาร ======

  y += 8;
  const bankName = record?.BANKLOCNAM?.split("/")[0];

  setFont("normal", 12);
  pdf.text("ชื่อธนาคาร", mL + 2, y);
  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.line(mL + 15, y + 1, mL + 65, y + 1);
  setFont("bold", 12);
  pdf.text(bankName, mL + 15, y);

  setFont("normal", 12);
  pdf.text(record?.BOOKNO || "", mL + 43, y);
  pdf.text("สาขา", mL + 66, y);
  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.line(mL + 72, y + 1, mL + 130, y + 1);
  setFont("bold", 12);
  pdf.text(record.BKADDR1, mL + 75, y);
  setFont("normal", 12);
  pdf.text("เช็คเลขที่", mL + 135, y);
  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.line(mL + 146, y + 1, mL + 166, y + 1);
  pdf.text("ลงวันที่", mL + 174, y);
  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.line(mL + 183, y + 1, mL + 198, y + 1);

  // ====== MEMO ======
  y += 8;
  setFont("normal", 12);
  pdf.text(record?.MEMO1 || "", mL + 2, y);

  // ====== เส้นคั่น (ก่อนส่วนลงนาม) ======
  y += 6;
  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.line(mL, y, mL + cW, y);

  // ====== กรอบส่วนลงนาม (ล้อมทั้ง 4 คอลัมน์) ======
  const sigH = 15;
  pdf.setLineWidth(0.25);
  pdf.rect(mL, y, cW, sigH);

  // ====== ลงนาม 4 คอลัมน์ ======
  y += 5;
  const sigCols = ["ผู้อนุมัติ", "ผู้ตรวจสอบ", "ผู้จ่ายเงิน", "ผู้รับเงิน"];
  const colW = cW / 4;

  sigCols.forEach((label, i) => {
    const colX = mL + i * colW;

    // เส้นแบ่งคอลัมน์ (ไม่วาดเส้นซ้ายสุด เพราะมีกรอบแล้ว)
    if (i > 0) {
      pdf.setLineWidth(0.25);
      pdf.line(colX, y - 5, colX, y - 5 + sigH);
    }

    setFont("normal", 13);
    pdf.text(`${label} :`, colX + 2, y);
    drawDottedLine(pdf, colX + 15, y + 0.5, colX + colW - 4);

    setFont("normal", 12);
    pdf.text("วันที่", colX + 2, y + 6);
    pdf.text("__/__/__", colX + 14, y + 6);
  });
};

// ============================================================
// MAIN EXPORT: generatePDF
// ใบเบิกเงินทดรองจ่าย
// ============================================================
export const generatePDF = async ({
  dataExport,
  selectedRows,
  billDetail,
  nameLawyers,
  companiesList,
  imageList,
  convertDateThai,
  currencyFormatPoint,
  advancePayData = null, // null = ไม่พิมพ์ใบเสร็จ | object = พิมพ์ใบเสร็จต่อท้าย
}) => {
  const pdf = new jsPDF();
  const currentUserId = parseInt(localStorage.getItem("USER_ID"));

  const groupedData = groupByTMBILL(selectedRows);

  // ============================================================
  // ✅ ถ้ามี advancePayData → วาดใบเสร็จหน้าแรกก่อนเลย
  // ============================================================
  let receiptDrawnFirst = false;

  if (advancePayData) {
    // ดึง companyData จาก record แรกใน selectedRows
    const firstRecord = selectedRows[0];
    const firstCompanyData = getCompanyData(
      companiesList,
      firstRecord.COMPANY_ID,
    );

    if (firstCompanyData) {
      // หน้าแรกของ PDF คือใบเสร็จ (ไม่ต้อง addPage)
      drawReceiptPage(pdf, {
        record: advancePayData,
        currencyFormatPoint,
        companyData: firstCompanyData,
        dataExport: dataExport[0],
      });
      receiptDrawnFirst = true;
    }
  }

  // ============================================================
  // วนลูปใบเบิกเงินทดรองจ่าย
  // ============================================================
  let pageIndex = 0;

  for (const [tmbill, records] of Object.entries(groupedData)) {
    const record = records[0];

    // ✅ ถ้าวาดใบเสร็จไปก่อนแล้ว → addPage ทุกครั้ง
    // ✅ ถ้าไม่มีใบเสร็จ → addPage เฉพาะตั้งแต่หน้าที่ 2 เป็นต้นไป (เดิม)
    if (receiptDrawnFirst || pageIndex !== 0) {
      pdf.addPage();
    }
    pageIndex++;

    // ============ VARIABLES ============
    let pdfPositionY = 0;
    const pdfPositionX = 10;
    const pdfPositionXCenter = 150;
    let imageWidth = 45;
    let imageHeight = 25;
    const imageWidthSign = 25;
    const imageHeightSign = 15;

    // ============ บริษัท ============
    const companyData = getCompanyData(companiesList, record.COMPANY_ID);
    if (!companyData) {
      console.warn(`ไม่พบข้อมูลบริษัทสำหรับ COMPANY_ID: ${record.COMPANY_ID}`);
      continue;
    }

    // ============ LOGO ============
    const imageUrl =
      companyData.id === 1
        ? logoLeasing
        : companyData.id === 2
          ? logoMoney
          : companyData.id === 3
            ? logoKSM
            : logoLeasing;

    if (companyData.id === 2) pdfPositionY += 5;
    else if (companyData.id === 3) imageHeight = 30;

    pdf.addImage(
      imageUrl,
      "PNG",
      pdfPositionXCenter,
      pdfPositionY,
      imageWidth,
      imageHeight,
    );

    // ============ HEADER INFO ============
    pdf.setFont("THSarabunNew", "bold");
    pdf.setFontSize(14);
    pdf.text(`วันที่พิมพ์ ${convertDateThai()}`, pdfPositionX, 10);
    pdf.text(`เลขที่อ้างอิง ${tmbill || "-"}`, pdfPositionX, 15);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      `${!record.flagPrint ? "[ต้นฉบับ]" : "[สำเนา]"}`,
      pdfPositionX + 90,
      10,
    );
    pdf.setTextColor(0, 0, 0);

    // ============ COMPANY INFO ============
    pdf.setFontSize(16);
    if (companyData.id === 1) {
      pdfPositionY += 30;
      pdf.text(
        `${companyData.company_name}`,
        pdfPositionXCenter - 11,
        pdfPositionY,
      );
      pdf.text(`${companyData.address}`, 90, (pdfPositionY += 8));
    } else if (companyData.id === 2) {
      pdfPositionY += 33;
      pdf.text(
        `${companyData.company_name}`,
        pdfPositionXCenter - 10,
        pdfPositionY,
      );
      pdf.text(
        `${companyData.address}`,
        pdfPositionXCenter - 54,
        (pdfPositionY += 8),
      );
    } else if (companyData.id === 3) {
      pdfPositionY += 25;
      pdf.text(
        `${companyData.company_name}`,
        pdfPositionXCenter - 40,
        (pdfPositionY += 3),
      );
      pdf.text(
        `${companyData.address}`,
        pdfPositionXCenter - 60,
        (pdfPositionY += 8),
      );
    }

    // ============ TITLE ============
    pdfPositionY += 10;
    pdf.text("ใบเบิกเงินทดรองจ่าย", pdfPositionX + 70, pdfPositionY);
    pdf.setTextColor(0, 0, 0);
    pdfPositionY += 5;

    // ============ TABLE ============
    const { tableData } = prepareTableData(
      tmbill,
      billDetail,
      convertDateThai,
      currencyFormatPoint,
    );
    if (tableData.length === 0) {
      console.warn(`ไม่มีข้อมูลสำหรับ TMBILL: ${tmbill}`);
      continue;
    }

    pdf.autoTable({
      head: [
        ["ลำดับ", "เลขที่สัญญา", "วันที่ขอเบิก", "รายการ", "จำนวนเบิก(บาท)"],
      ],
      body: tableData,
      startY: pdfPositionY,
      styles: { font: "THSarabunNew", fontSize: 14, cellPadding: 3 },
      headStyles: {
        fillColor: [0, 102, 204],
        textColor: [255, 255, 255],
        fontSize: 14,
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 20 },
        1: { halign: "center", cellWidth: 30 },
        2: { halign: "center", cellWidth: 35 },
        3: { halign: "left", cellWidth: 70 },
        4: { halign: "right", cellWidth: 35 },
      },
      margin: { top: 10, left: 10, right: 10 },
      didParseCell(data) {
        if (
          data.section === "body" &&
          data.cell.raw === "รวม" &&
          data.column.index === 3
        )
          data.cell.styles.fontStyle = "bold";
        const isLast = data.row.index === data.table.body.length - 1;
        if (isLast && data.section === "body" && data.cell.raw === "รวมทั้งหมด")
          data.cell.styles.fontStyle = "bold";
      },
    });

    const finalY = pdf.lastAutoTable.finalY;
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("THSarabunNew", "normal");
    pdf.setFontSize(14);

    // ============ ลายเซ็นผู้เบิก ============
    const lawyer = nameLawyers?.find((item) => item.id === record.userId);
    const lawyerSignature = await findSignatureImage(imageList, record.userId);

    if (lawyerSignature)
      pdf.addImage(
        lawyerSignature,
        "PNG",
        55,
        finalY + 7,
        imageWidthSign,
        imageHeightSign,
      );

    pdf.text(
      "ลงชื่อผู้เบิก...................................",
      40,
      finalY + 20,
    );
    pdf.text(`${lawyer?.FNAME || ""}  ${lawyer?.LNAME || ""}`, 45, finalY + 27);
    if (lawyer?.book_bank) pdf.text(`${lawyer.book_bank}`, 45, finalY + 32);
    if (lawyer?.telp) pdf.text(`${lawyer.telp}`, 45, finalY + 37);

    // ============ หาผู้อนุมัติ ============
    let approverInfo = null;
    let approverSignature = null;

    if (record.approvalStatus === 1 && record.approvalBy) {
      const approverData = nameLawyers?.find(
        (l) => l.id === parseInt(record.approvalBy),
      );
      if (approverData) {
        approverInfo = {
          approverId: parseInt(record.approvalBy),
          approverData,
        };
        approverSignature = await findSignatureImage(
          imageList,
          approverInfo.approverId,
        );
      }
    } else if (canApprove(currentUserId)) {
      approverInfo = getApproverInfo(currentUserId, nameLawyers);
      if (approverInfo)
        approverSignature = await findSignatureImage(
          imageList,
          approverInfo.approverId,
        );
    }

    // ============ ลายเซ็นผู้อนุมัติ ============
    pdf.text(
      "ลงชื่อผู้อนุมัติ...................................",
      120,
      finalY + 20,
    );

    if (record.approvalStatus === 1 && record.approvalDate && approverInfo) {
      if (approverSignature)
        pdf.addImage(
          approverSignature,
          "PNG",
          140,
          finalY + 4,
          imageWidthSign,
          imageHeightSign,
        );
      const approverFullName = `${approverInfo.approverData?.FNAME || ""} ${approverInfo.approverData?.LNAME || ""}`;
      pdf.text(`(${approverFullName})`, 130, finalY + 27);
      pdf.text(
        `วันที่อนุมัติ ${convertDateThai(dayjs(record.approvalDate))}`,
        125,
        finalY + 32,
      );
    } else {
      pdf.text("(                                     )", 125, finalY + 27);
    }

    pdf.text(
      "ลงชื่อผู้ตรวจ..................................",
      120,
      finalY + 55,
    );
  }

  // ============ เปิด PDF ============
  const pdfBlob = pdf.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const newWindow = window.open(pdfUrl);
  if (newWindow) {
    newWindow.onload = () => newWindow.print();
  } else {
    alert("กรุณาปิดการบล็อกป๊อปอัปเพื่อใช้งานฟังก์ชันนี้");
  }
};
